import { useState } from "react";
import { FiCopy, FiLoader } from "react-icons/fi";
import CustomButton from "../../components/common/CustomButton";
import ImageViewModal from "../../components/vendors/ImageViewModal";
import { useNavigate } from "react-router-dom";
import { useVendorProfile } from "../../custom-hooks/queries";
import { getCleanedUrl } from "../../utils/HelperMethod";
import { toast } from "react-toastify";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { data: profileData, isLoading } = useVendorProfile();

  const [modalState, setModalState] = useState({
    isOpen: false,
    imageSrc: null,
    title: "",
  });

  const openModal = (imageSrc, title) => {
    setModalState({
      isOpen: true,
      imageSrc,
      title,
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      imageSrc: null,
      title: "",
    });
  };

  const logo = getCleanedUrl(profileData?.data?.logoUrl);

  const copyPublicProfileUrl = async () => {
    // if (!profile.slug) return;

    const url = `${window.location.origin}/vendors/${profile.slug}`;

    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied!");
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 sm:px-6 py-6 sm:py-10">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Profile</h1>
        <p className="text-gray-500 text-sm mt-1">
          View your business profile information
        </p>

        {/* Skeleton Loading */}
        <div className="mt-6 sm:mt-8 bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0 animate-pulse">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gray-200" />
              <div>
                <div className="h-5 w-32 bg-gray-200 rounded mb-2" />
                <div className="h-4 w-48 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="h-10 w-20 bg-gray-200 rounded-lg" />
          </div>

          <hr className="my-6 border-gray-200" />

          <div className="border rounded-xl overflow-hidden mt-4 sm:mt-6 animate-pulse">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-2 border-b last:border-b-0"
              >
                <div className="p-3 sm:p-4 border-r-0 md:border-r">
                  <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-32 bg-gray-200 rounded" />
                </div>
                <div className="p-3 sm:p-4">
                  <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-40 bg-gray-200 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const profile = profileData?.data || profileData || {};

  return (
    <div className="w-full px-4 sm:px-6 py-6 sm:py-10">
      {/* PAGE TITLE */}
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Profile</h1>
      <p className="text-gray-500 text-sm mt-1">
        View your business profile information
      </p>

      {/* MAIN CARD */}
      <div className="mt-6 sm:mt-8 bg-white border border-[#E5E7EB] rounded-2xl shadow-sm p-4 sm:p-6">
        {/* TOP SECTION */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
          <div className="flex items-center gap-3 sm:gap-4">
            {/* AVATAR */}
            <img
              src={logo}
              alt="Business Logo"
              className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover"
            />

            <div>
              <p className="text-base sm:text-lg font-semibold text-gray-900">
                {profile.businessName || "Business Name"}
              </p>
              <p className="text-gray-500 text-xs sm:text-sm">
                {profile.businessDescription || "No description available"}
              </p>
            </div>
          </div>

          {/* EDIT BUTTON */}
          <div className="flex gap-2">
            <CustomButton variant="secondary" onClick={copyPublicProfileUrl}>
              Copy Public Profile URL
            </CustomButton>

            <CustomButton onClick={() => navigate("/vendor/profile/edit")}>
              Edit
            </CustomButton>
          </div>
        </div>

        {/* DIVIDER */}
        <hr className="my-6 border-gray-200" />

        {/* DETAILS GRID */}
        <div className="border rounded-xl overflow-hidden mt-4 sm:mt-6">
          {/* ROW 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 border-b">
            <DetailCell label="Tax ID" value={profile.tax_id || "—"} />
            <DetailCell
              label="Business License Number"
              value={profile.businessLicenseNumber || "—"}
            />
          </div>

          {/* ROW 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 border-b">
            <DetailCell
              label="Contact Name"
              value={profile.contact_name || "—"}
            />
            <DetailCell label="Email" value={profile.email || "—"} />
          </div>

          {/* ROW 3 */}
          <div className="grid grid-cols-1 md:grid-cols-2 border-b">
            <DetailCell label="Phone" value={profile.phone || "—"} />
            <DetailCell
              label="Business Address"
              value={profile.businessAddress || "—"}
            />
          </div>

          {/* FULL ROW — Description */}
          <div className="p-3 sm:p-4 border-b">
            <p className="text-sm font-semibold text-gray-700">
              Business Description
            </p>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              {profile.businessDescription || "No description available"}
            </p>
          </div>

          {/* FINAL ROW — IDs */}
          <div className="grid grid-cols-1 md:grid-cols-2">
            <AttachmentCell
              label="Government ID"
              hasFile={!!profile.governmentIdUrl}
              onView={() =>
                openModal(
                  getCleanedUrl(profile.governmentIdUrl),
                  "Government ID",
                )
              }
            />
            <AttachmentCell
              label="Liability Insurance"
              hasFile={!!profile.liabilityInsuranceUrl}
              onView={() =>
                openModal(
                  getCleanedUrl(profile.liabilityInsuranceUrl),
                  "Liability Insurance",
                )
              }
            />
          </div>
        </div>
      </div>

      {/* IMAGE VIEW MODAL */}
      <ImageViewModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        imageSrc={modalState.imageSrc}
        title={modalState.title}
      />
    </div>
  );
}

/* ------------------- HELPER COMPONENTS ------------------- */

function DetailCell({ label, value }) {
  return (
    <div className="p-3 sm:p-4 border-r-0 md:border-r md:last:border-r-0 border-b md:border-b-0 last:border-b-0">
      <p className="text-xs sm:text-sm font-semibold text-gray-700">{label}</p>
      <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">
        {value}
      </p>
    </div>
  );
}

function AttachmentCell({ label, hasFile, onView }) {
  return (
    <div className="p-3 sm:p-4 border-r-0 md:border-r md:last:border-r-0 border-b md:border-b-0 last:border-b-0">
      <p className="text-xs sm:text-sm font-semibold text-gray-700">{label}</p>

      {hasFile ? (
        <button
          onClick={onView}
          className="text-[#01B9C6] text-xs sm:text-sm font-medium mt-1 hover:underline"
        >
          View
        </button>
      ) : (
        <p className="text-xs sm:text-sm text-gray-400 mt-1">Not uploaded</p>
      )}
    </div>
  );
}
